import { Container } from '@chakra-ui/react'
import {
  ContentNotFound,
  GroupsAndToursCollection,
  Layout,
  RelatedEvents,
  SsmFriendlyCardAdvantages,
} from '@common'
import { ISSRPageData } from '@interfaces'
import { useInViewMotion } from '@lib/hooks'
import { fadeIn } from '@lib/motion'
import { PageService } from '@services'
import { MotionBox, MotionText } from '@ui'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

const GroupsAndToursScreen = ({
  pageData,
  global: { navigation },
}: ISSRPageData) => {
  const { t } = useTranslation()
  const { asPath } = useRouter()
  const { controls, ref } = useInViewMotion(true)

  if (pageData.error) {
    return (
      <Layout
        title={t('visit:groupsAndTours.title')}
        navigation={navigation}
        path={asPath}
      >
        <ContentNotFound infoType="serverSideError" />
      </Layout>
    )
  }

  return (
    <Layout
      title={t('visit:groupsAndTours.title')}
      navigation={navigation}
      path={asPath}
    >
      <MotionBox
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={fadeIn.animationContainer}
      >
        <Container
          as="section"
          pt={{ base: '65px', lg: '70px' }}
          pb={{ base: 'padding.primary2', lg: 'padding.primary4' }}
        >
          <MotionText
            variants={fadeIn.up}
            as="h2"
            fontSize={{ base: '2xl', lg: '3xl' }}
            color="red.primary"
            mb={{ base: 1, lg: '6px' }}
          >
            {pageData.data.hero?.subTitle}
          </MotionText>
          <MotionText
            variants={fadeIn.up}
            as="h1"
            fontSize={{ base: '4xl', lg: '5xl' }}
            color="white.primary3"
            lineHeight="shorter"
            fontWeight="bold"
          >
            {pageData.data.hero?.title}
          </MotionText>
          <MotionText
            variants={fadeIn.up}
            as="p"
            maxW="610px"
            fontWeight="light"
          >
            {pageData.data.hero?.description}
          </MotionText>
          <GroupsAndToursCollection
            tourCategories={pageData.data.tourCategories}
          />
        </Container>
      </MotionBox>
      <RelatedEvents
        title={t('visit:relatedEvents.title')}
        collection={pageData.data.relatedExhibitionAndEvents}
      />
      <SsmFriendlyCardAdvantages mb={{ base: 'padding.primary2', lg: '0' }} />
    </Layout>
  )
}

export default GroupsAndToursScreen

export async function getServerSideProps({ locale }) {
  const pageData = {
    data: {
      hero: null,
      tourCategories: null,
      relatedExhibitionAndEvents: null,
    },
    error: null,
  }

  try {
    const groupAndToursPageData = await PageService.getGroupAndTourPageData(
      locale
    )

    if (groupAndToursPageData.tourCategories) {
      pageData.data.tourCategories = groupAndToursPageData.tourCategories
    }

    if (groupAndToursPageData.hero) {
      pageData.data.hero = groupAndToursPageData.hero
    }

    if (groupAndToursPageData.relatedExhibitionAndEvents) {
      pageData.data.relatedExhibitionAndEvents =
        groupAndToursPageData.relatedExhibitionAndEvents
    }
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData },
  }
}
